import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { signIn, signInWithUsername } from '../services/authService';
import Spinner from '../../../shared/components/Spinner';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useApprovalStatus } from '../hooks/useApprovalStatus';
import { ROLES } from '../../../shared/constant/roles';

function LoginPage() {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const isTeacherRole = role === ROLES.TEACHER;
  const shouldCheckApproval = Boolean(user) && isTeacherRole;
  const { approved, loading: approvalLoading } = useApprovalStatus(shouldCheckApproval);

  // A username or employee ID (students and teachers), or an email (admins, and teachers who
  // haven't been given a username yet).
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (authLoading || (shouldCheckApproval && approvalLoading)) {
    return <Spinner />;
  }

  // Already signed in — send the account to wherever it belongs instead of
  // showing the login form again.
  if (user) {
    if (role === ROLES.STUDENT) {
      return <Navigate to="/student-dashboard" replace />;
    }
    if (isTeacherRole && !approved) {
      return <Navigate to="/inactive-account" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = identifier.trim();
      if (trimmed.includes('@')) {
        await signIn(trimmed, password);
      } else {
        await signInWithUsername(trimmed, password);
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col bg-gray-200 sm:items-center sm:justify-center sm:px-4 sm:py-8">
      <form onSubmit={handleSubmit} className="w-full h-dvh sm:h-auto sm:max-w-sm bg-white sm:rounded-lg sm:border sm:border-gray-200 p-8 space-y-3 flex flex-col justify-center sm:justify-normal">
        <div className="flex flex-col items-center mb-4">
          <img src="/images/pawigrade.png" alt="Logo" className="h-32 w-auto" />
          <p className="text-3xl font-bold">
            <span className="text-[#4ea6d0]">Pawi</span>
            <span className="text-[#0b4a71]">Grade</span>
          </p>
          <p className="text-md text-gray-500">Calauag National High School</p>
        </div>

        <div>
          <h1 className="text-xl font-semibold text-[#0b4a71]">Welcome back</h1>
          <p className="text-sm text-gray-500">Log in to manage your account</p>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <div className="space-y-1">
          {/* <label htmlFor="identifier" className="text-md font-medium text-[#0b4a71]">Username/Email</label> */}
          <input
            id="identifier"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            placeholder="Enter your username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="w-full rounded-md border border-[#4ea6d0] px-3 py-2 text-lg text-[#0b4a71] focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          />
        </div>

        <div className="space-y-1">
          {/* <label htmlFor="password" className="text-md font-medium text-[#0b4a71]">Password</label> */}
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-[#4ea6d0] px-3 py-2 pr-10 text-lg text-[#0b4a71] focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4ea6d0] text-white rounded-md py-4 text-lg font-medium hover:bg-[#0b4a71] active:bg-[#0b4a71] disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>

        <p className="text-sm text-gray-500 text-center">
          Need an account or password reset? Contact the administrator.
        </p>
      </form>
    </div>
  );
}

export default LoginPage;
